import React, { useEffect, useMemo, useState } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import api from '../../utils/apiConfig';
import { useLocation } from '../../contexts/LocationContext';

const currency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));
const fmtDate = (d) => new Date(d).toLocaleDateString();

const Reports = () => {
  const { selectedLocation } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [range, setRange] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportRange, setExportRange] = useState('7d');
  const [exporting, setExporting] = useState(false);
  const [exportOpts, setExportOpts] = useState({
    kpis: true,
    ordersByDay: true,
    itemsAddedByDay: true,
    topSelling: true,
    recentOrders: true,
    recentDeliveries: true,
    recentItemsAdded: true,
  });

  const locationId = selectedLocation?._id || selectedLocation?.id;

  const load = async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/reports/overview/${locationId}`, { params: { range } });
      if (res.data?.success) {
        setData(res.data.data);
      } else {
        setError(res.data?.message || 'Failed to load reports');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, range]);

  const kpis = useMemo(() => data?.kpis || {}, [data]);
  const series = useMemo(() => data?.series || [], [data]);
  const topSelling = useMemo(() => data?.topSelling || [], [data]);
  const recentOrders = useMemo(() => data?.recentOrders || [], [data]);
  const recentDeliveries = useMemo(() => data?.recentDeliveries || [], [data]);
  const itemsAddedSeries = useMemo(() => data?.itemsAddedSeries || [], [data]);
  const recentInventoryAdds = useMemo(() => data?.recentInventoryAdds || [], [data]);

  const csvEscape = (val) => {
    const s = val === null || val === undefined ? '' : String(val);
    if (/[",\n]/.test(s)) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const buildCsv = (reportData, opts) => {
    const lines = [];
    lines.push(`Location ID,${csvEscape(locationId)}`);
    lines.push(`Range,${csvEscape(exportRange)}`);
    lines.push('');

    if (opts.kpis) {
      lines.push('Section,KPIs');
      lines.push('Metric,Value');
      lines.push(`Orders,${csvEscape(reportData.kpis?.ordersCount ?? 0)}`);
      lines.push(`Revenue,${csvEscape(reportData.kpis?.revenue ?? 0)}`);
      lines.push(`New Customers,${csvEscape(reportData.kpis?.newCustomers ?? 0)}`);
      lines.push(`Deliveries Completed,${csvEscape(reportData.kpis?.deliveriesCompleted ?? 0)}`);
      lines.push(`Items Added,${csvEscape(reportData.kpis?.itemsAdded ?? 0)}`);
      lines.push('');
    }

    if (opts.ordersByDay) {
      lines.push('Section,Orders by Day');
      lines.push('Date,Orders,Revenue');
      (reportData.series || []).forEach(d => {
        lines.push([d.date, d.orders, d.revenue].map(csvEscape).join(','));
      });
      lines.push('');
    }

    if (opts.itemsAddedByDay) {
      lines.push('Section,Items Added by Day');
      lines.push('Date,Items Added');
      (reportData.itemsAddedSeries || []).forEach(d => {
        lines.push([d.date, d.itemsAdded].map(csvEscape).join(','));
      });
      lines.push('');
    }

    if (opts.topSelling) {
      lines.push('Section,Top Selling Items');
      lines.push('Inventory ID,Name,SKU,Quantity');
      (reportData.topSelling || []).forEach(i => {
        lines.push([i.inventoryId, i.name, i.sku, i.quantity].map(csvEscape).join(','));
      });
      lines.push('');
    }

    if (opts.recentOrders) {
      lines.push('Section,Recent Orders');
      lines.push('Order ID,Order Number,Status,Total Amount,Created At,Customer');
      (reportData.recentOrders || []).forEach(o => {
        lines.push([o.id, o.orderNumber, o.status, o.totalAmount, o.createdAt, o.customerName].map(csvEscape).join(','));
      });
      lines.push('');
    }

    if (opts.recentDeliveries) {
      lines.push('Section,Recent Deliveries');
      lines.push('Delivery ID,Status,Order Number,Created At');
      (reportData.recentDeliveries || []).forEach(d => {
        lines.push([d.id, d.status, d.orderNumber, d.createdAt].map(csvEscape).join(','));
      });
      lines.push('');
    }

    if (opts.recentItemsAdded) {
      lines.push('Section,Recent Items Added');
      lines.push('Inventory ID,Name,SKU,Created At');
      (reportData.recentInventoryAdds || []).forEach(it => {
        lines.push([it.id, it.name, it.sku, it.createdAt].map(csvEscape).join(','));
      });
      lines.push('');
    }

    return lines.join('\n');
  };

  const fetchForRange = async (locId, r) => {
    const res = await api.get(`/reports/overview/${locId}`, { params: { range: r } });
    if (!res.data?.success) throw new Error(res.data?.message || 'Failed to fetch export data');
    return res.data.data;
  };

  const handleExport = async () => {
    if (!locationId) return;
    try {
      setExporting(true);
      const shouldRefetch = exportRange !== range;
      const exportData = shouldRefetch ? await fetchForRange(locationId, exportRange) : (data || {});
      const csv = buildCsv(exportData, exportOpts);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reports_export_${locationId}_${exportRange}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (e) {
      setError(e.message || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Reports
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="range-label">Date Range</InputLabel>
            <Select
              labelId="range-label"
              value={range}
              label="Date Range"
              onChange={(e) => setRange(e.target.value)}
            >
              <MenuItem value="24h">Last 24 hours</MenuItem>
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => { setExportRange(range); setExportOpen(true); }} disabled={!data}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">Loading reports...</Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Orders</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{kpis.ordersCount ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Revenue</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{currency(kpis.revenue)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">New Customers</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{kpis.newCustomers ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Deliveries</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{kpis.deliveriesCompleted ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Items Added</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{kpis.itemsAdded ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Top Selling Items</Typography>
                  {topSelling.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No data</Typography>
                  ) : (
                    <List dense>
                      {topSelling.map((i) => (
                        <React.Fragment key={i.inventoryId}>
                          <ListItem secondaryAction={<Chip size="small" label={`Qty ${i.quantity}`} />}> 
                            <ListItemText primary={i.name} secondary={i.sku} />
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Orders by Day</Typography>
                  {series.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No data</Typography>
                  ) : (
                    <List dense>
                      {series.map((d) => (
                        <React.Fragment key={d.date}>
                          <ListItem secondaryAction={<Chip size="small" label={`${currency(d.revenue)}`} />}>
                            <ListItemText primary={d.date} secondary={`Orders: ${d.orders}`} />
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Items Added by Day</Typography>
                  {itemsAddedSeries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No data</Typography>
                  ) : (
                    <List dense>
                      {itemsAddedSeries.map((d) => (
                        <React.Fragment key={d.date}>
                          <ListItem secondaryAction={<Chip size="small" label={`+${d.itemsAdded}`} color="primary" />}> 
                            <ListItemText primary={d.date} />
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Recent Items Added</Typography>
                  {recentInventoryAdds.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No recent items</Typography>
                  ) : (
                    <List dense>
                      {recentInventoryAdds.map((it) => (
                        <React.Fragment key={it.id}>
                          <ListItem secondaryAction={<Chip size="small" label={fmtDate(it.createdAt)} />}> 
                            <ListItemText primary={it.name || '—'} secondary={it.sku} />
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Recent Orders</Typography>
                  {recentOrders.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No recent orders</Typography>
                  ) : (
                    <List dense>
                      {recentOrders.map((o) => (
                        <React.Fragment key={o.id}>
                          <ListItem secondaryAction={<Chip size="small" label={currency(o.totalAmount)} />}> 
                            <ListItemText primary={`#${o.orderNumber} — ${o.customerName || '—'}`} secondary={`${o.status || ''} • ${fmtDate(o.createdAt)}`} />
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Recent Deliveries</Typography>
                  {recentDeliveries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No recent deliveries</Typography>
                  ) : (
                    <List dense>
                      {recentDeliveries.map((d) => (
                        <React.Fragment key={d.id}>
                          <ListItem secondaryAction={<Chip size="small" label={d.status} color={d.status === 'DELIVERED' ? 'success' : 'default'} />}> 
                            <ListItemText primary={`Order #${d.orderNumber || '—'}`} secondary={fmtDate(d.createdAt)} />
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      <Dialog 
        open={exportOpen} 
        onClose={() => setExportOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
      <DialogTitle>Export Reports to CSV</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="export-range-label">Date Range</InputLabel>
            <Select
              labelId="export-range-label"
              value={exportRange}
              label="Date Range"
              onChange={(e) => setExportRange(e.target.value)}
            >
              <MenuItem value="24h">Last 24 hours</MenuItem>
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <FormGroup>
          <FormControlLabel control={<Checkbox checked={exportOpts.kpis} onChange={e => setExportOpts(o => ({ ...o, kpis: e.target.checked }))} />} label="KPIs" />
          <FormControlLabel control={<Checkbox checked={exportOpts.ordersByDay} onChange={e => setExportOpts(o => ({ ...o, ordersByDay: e.target.checked }))} />} label="Orders by Day" />
          <FormControlLabel control={<Checkbox checked={exportOpts.itemsAddedByDay} onChange={e => setExportOpts(o => ({ ...o, itemsAddedByDay: e.target.checked }))} />} label="Items Added by Day" />
          <FormControlLabel control={<Checkbox checked={exportOpts.topSelling} onChange={e => setExportOpts(o => ({ ...o, topSelling: e.target.checked }))} />} label="Top Selling Items" />
          <FormControlLabel control={<Checkbox checked={exportOpts.recentOrders} onChange={e => setExportOpts(o => ({ ...o, recentOrders: e.target.checked }))} />} label="Recent Orders" />
          <FormControlLabel control={<Checkbox checked={exportOpts.recentDeliveries} onChange={e => setExportOpts(o => ({ ...o, recentDeliveries: e.target.checked }))} />} label="Recent Deliveries" />
          <FormControlLabel control={<Checkbox checked={exportOpts.recentItemsAdded} onChange={e => setExportOpts(o => ({ ...o, recentItemsAdded: e.target.checked }))} />} label="Recent Items Added" />
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setExportOpen(false)} disabled={exporting}>Cancel</Button>
        <Button onClick={handleExport} variant="contained" disabled={exporting || !locationId}>
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reports;
